import { useRef } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { ASSETS } from "../lib/brand";

const CHAPTERS = [
  {
    n: "01",
    key: "desapega",
    title: "Desapega.",
    lead: "Uma peça esquecida no armário é uma história pausada. Para mim, reeditar é um ato de estilo.",
    body: "A moda circular, no meu entendimento, começa no gesto de escolher com consciência. É o inverso do fast fashion: devolver valor ao que já foi criado com propósito e afeto.",
    image: ASSETS.phone,
    align: "right",
  },
  {
    n: "02",
    key: "renova",
    title: "Renova.",
    lead: "Renovar é reeditar o desejo. É devolver ao guarda-roupa a força do que já tem alma.",
    body: "Meu olhar transforma peças em narrativa. Cada composição eu penso como uma edição especial — com direção, contexto e presença.",
    image: ASSETS.rack,
    align: "left",
  },
  {
    n: "03",
    key: "transforma",
    title: "Transforma.",
    lead: "Vestir uma peça de segunda vida é assinar uma outra forma de existir na moda — mais lenta, mais consciente, mais sua.",
    body: "Eu transformo o brechó em cultura. Consultoria, curadoria pessoal, palestras e educação — meu jeito de abrir espaço para uma nova geração que veste com propósito.",
    image: ASSETS.hangers,
    align: "right",
  },
];

function Chapter({ chapter, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);

  const isRight = chapter.align === "right";

  return (
    <div
      ref={ref}
      data-testid={`chapter-${chapter.key}`}
      className="relative border-t border-ink-800/15 pt-16 sm:pt-24 pb-24 sm:pb-40"
    >
      {/* Giant chapter number */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
        className={`font-serif leading-none text-brick-500/15 select-none pointer-events-none absolute ${
          isRight ? "-top-8 left-4 sm:left-10" : "-top-8 right-4 sm:right-10"
        }`}
        style={{ fontSize: "clamp(9rem, 22vw, 22rem)" }}
      >
        {chapter.n}
      </motion.div>

      <div className="relative grid grid-cols-12 gap-6 lg:gap-16 items-start">
        {/* Text column */}
        <div
          className={`col-span-12 lg:col-span-6 ${
            isRight ? "lg:order-1" : "lg:order-2"
          }`}
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.1 }}
            className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/60 mb-8"
          >
            <span className="w-8 h-px bg-brick-500" />
            <span>Capítulo {chapter.n}</span>
          </motion.div>

          <div className="overflow-hidden">
            <motion.h2
              initial={{ y: "110%" }}
              animate={inView ? { y: 0 } : {}}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className="font-serif text-brick-500 italic font-medium leading-[0.9] tracking-tight"
              style={{ fontSize: "clamp(3.5rem, 8vw, 8rem)" }}
              data-testid={`chapter-title-${chapter.key}`}
            >
              {chapter.title}
            </motion.h2>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-10 font-serif text-2xl sm:text-3xl leading-snug text-ink-800 max-w-xl"
          >
            {chapter.lead}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.65 }}
            className="mt-8 font-sans text-base sm:text-lg leading-relaxed text-ink-800/70 max-w-lg"
          >
            {chapter.body}
          </motion.p>
        </div>

        {/* Image column */}
        <div
          className={`col-span-12 lg:col-span-6 ${
            isRight ? "lg:order-2" : "lg:order-1"
          } relative`}
        >
          <div className="relative overflow-hidden aspect-[4/5] bg-cream-200">
            <motion.img
              src={chapter.image}
              alt={`Capítulo ${chapter.n} — ${chapter.title}`}
              className="absolute inset-0 w-full h-full object-cover"
              initial={{ scale: 1.2, opacity: 0 }}
              animate={inView ? { scale: 1, opacity: 1 } : {}}
              transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
              style={{ y }}
              data-testid={`chapter-image-${chapter.key}`}
            />
            <motion.div
              initial={{ scaleY: 1 }}
              animate={inView ? { scaleY: 0 } : {}}
              transition={{ duration: 1.4, ease: [0.76, 0, 0.24, 1], delay: 0.1 }}
              style={{ transformOrigin: "top" }}
              className="absolute inset-0 bg-brick-500 z-10"
            />
          </div>

          <div className="mt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/60">
            <span>Fig. 0{index + 1}</span>
            <span>Herida — Editorial · MMXXVI</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <section
      id="proposito"
      data-testid="proposito-section"
      className="relative bg-cream-100"
    >
      <div className="mx-auto max-w-[1600px] px-6 sm:px-10 pt-24 sm:pt-40 pb-6">
        <div className="grid grid-cols-12 gap-6 lg:gap-16 items-end">
          <div className="col-span-12 lg:col-span-6">
            <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/60 mb-8">
              <span className="w-8 h-px bg-brick-500" />
              <span>Propósito — Três Rituais</span>
            </div>
            <h3
              className="font-serif text-ink-800 leading-[0.95] tracking-tight"
              style={{ fontSize: "clamp(2.5rem, 5.5vw, 5.5rem)" }}
              data-testid="proposito-title"
            >
              Meu brechó não é <br />
              <span className="italic-serif text-brick-500">só um lugar</span> —
              <br />é uma <span className="italic-serif text-brick-500">postura</span>.
            </h3>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:col-start-8">
            <p className="font-sans text-base sm:text-lg leading-relaxed text-ink-800/70 max-w-md">
              Eu acredito que moda não se descarta — ela circula.
              Três atos guiam meu pensamento e minha prática de construir
              uma outra relação com o que se veste.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1600px] px-6 sm:px-10">
        {CHAPTERS.map((c, i) => (
          <Chapter key={c.key} chapter={c} index={i} />
        ))}
      </div>
    </section>
  );
}
